import { GRID_LIMITS } from './grid.js';
export const DETECT_LIMITS = { minPitch: 8, maxPitch: 400, minConfidence: 0.3, harmonicMargin: 0.85, weakPeak: 0.3, minLines: 4, minFoundShare: 0.6, maxDrift: 6, offMiddle: 0.06 };
const lum = (d, i) => 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
/**
 * The two raw profiles in one pass, keeping five rows of luminance in a
 * ring so a 4,000 × 4,000 map costs its own pixels and nothing more.
 * `x[c]` answers a vertical line at column c; `y[r]` a horizontal one at row r.
 */
export function edgeProfiles(img) {
    const { width: w, height: h, data } = img;
    const x = new Float64Array(w);
    const y = new Float64Array(h);
    const ring = Array.from({ length: 5 }, () => new Float64Array(w));
    const rowAt = (r) => ring[((r % 5) + 5) % 5];
    for (let r = 0; r < h; r += 1) {
        const row = rowAt(r);
        const base = r * w * 4;
        for (let c = 0; c < w; c += 1)
            row[c] = lum(data, base + c * 4);
        for (let c = 2; c < w - 2; c += 1)
            x[c] = x[c] + row[c - 2] + row[c + 2] - 2 * row[c];
        // The row two back is now the middle of a complete five-row window.
        if (r >= 4) {
            const mid = r - 2;
            const up = rowAt(r - 4);
            const at = rowAt(mid);
            let sum = 0;
            for (let c = 0; c < w; c += 1)
                sum += up[c] + row[c] - 2 * at[c];
            y[mid] = Math.abs(sum);
        }
    }
    for (let c = 0; c < w; c += 1)
        x[c] = Math.abs(x[c]) / Math.max(1, h);
    for (let r = 0; r < h; r += 1)
        y[r] = y[r] / Math.max(1, w);
    return { x, y };
}
/** The profile less its own local mean, floored at zero: a line stands out, the ground's shading drops away. */
export function detrend(p, radius = 6) {
    const n = p.length;
    const prefix = new Float64Array(n + 1);
    for (let i = 0; i < n; i += 1)
        prefix[i + 1] = prefix[i] + p[i];
    const out = new Float64Array(n);
    for (let i = 0; i < n; i += 1) {
        const a = Math.max(0, i - radius);
        const b = Math.min(n, i + radius + 1);
        out[i] = Math.max(0, p[i] - (prefix[b] - prefix[a]) / (b - a));
    }
    return out;
}
/** The strongest value within `r` of each point: a comb tooth a pixel or two off a drifting line still lands on it. */
function dilate(p, r) {
    const out = new Float64Array(p.length);
    for (let i = 0; i < p.length; i += 1) {
        let m = 0;
        for (let k = Math.max(0, i - r); k <= Math.min(p.length - 1, i + r); k += 1)
            if (p[k] > m)
                m = p[k];
        out[i] = m;
    }
    return out;
}
/**
 * How well the profile matches itself shifted by `lag`, against how well it
 * matches itself unshifted: near 1 for a train of lines `lag` apart, low for
 * scattered edges. Each line is compared with its neighbour only, so a scan
 * whose spacing changes along the axis never accumulates an error.
 */
function autocorrelation(q, qd, lag) {
    let num = 0;
    let den = 0;
    const whole = Math.floor(lag);
    const frac = lag - whole;
    for (let i = 0; i < q.length; i += 1) {
        const v = q[i];
        if (v === 0)
            continue;
        den += v * qd[i];
        const j = i + whole;
        if (j + 1 < q.length)
            num += v * (qd[j] * (1 - frac) + qd[j + 1] * frac);
    }
    return den > 0 ? num / den : 0;
}
/**
 * The profile under a small triangular kernel, for placing lines: a thick,
 * soft line gives the second difference a flat top several samples wide,
 * and only a kernel that weighs the middle most puts the peak at its centre.
 */
function smooth(p, r = 3) {
    const out = new Float64Array(p.length);
    for (let i = 0; i < p.length; i += 1) {
        let sum = 0;
        for (let d = -r; d <= r; d += 1)
            sum += (r + 1 - Math.abs(d)) * (p[i + d] ?? 0);
        out[i] = sum;
    }
    return out;
}
/** Sub-pixel peak by a parabola through the three samples around it. */
function refine(q, i) {
    const a = q[i - 1] ?? 0;
    const b = q[i];
    const c = q[i + 1] ?? 0;
    const den = a - 2 * b + c;
    return den < 0 ? i + Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / den)) : i;
}
/** One axis from its raw profile: the pitch, the phase, every line, or null when no grid stands out. */
export function detectAxis(profile) {
    const q = detrend(profile);
    const n = q.length;
    const mean = q.reduce((s, v) => s + v, 0) / Math.max(1, n);
    if (!(mean > 0))
        return null;
    // A line may sit a few pixels from where its neighbour's spacing puts it: the match tolerates that in proportion
    // to the pitch — up to six pixels, never enough to reach the next line, so half the pitch still matches nothing.
    const dilations = Array.from({ length: DETECT_LIMITS.maxDrift + 1 }, (_, r) => (r === 0 ? q : dilate(q, r)));
    const qdFor = (pitch) => dilations[Math.max(1, Math.min(DETECT_LIMITS.maxDrift, Math.floor(pitch / 8)))];
    // At least four squares across, or a grid is not what this is.
    const maxPitch = Math.min(DETECT_LIMITS.maxPitch, Math.floor(n / DETECT_LIMITS.minLines));
    if (maxPitch < DETECT_LIMITS.minPitch)
        return null;
    // Every whole pitch; a multiple of the true one matches too, so the SMALLEST within a margin of the best is taken.
    const scores = [];
    for (let p = DETECT_LIMITS.minPitch; p <= maxPitch; p += 1)
        scores.push({ pitch: p, score: autocorrelation(q, qdFor(p), p) });
    const top = Math.max(...scores.map((x) => x.score));
    const fundamental = scores.find((x) => x.score >= DETECT_LIMITS.harmonicMargin * top);
    // The tolerance that let a near pitch match also lets a pitch a few pixels short of the true one score nearly as
    // well, and the smallest within the margin is taken (issue 1024: 58 for 62.7 on a painted map). So the pitch is
    // settled again across that whole tolerance, against the profile matched to within a pixel.
    const reach = Math.max(1, Math.min(DETECT_LIMITS.maxDrift, Math.floor(fundamental.pitch / 8)));
    let chosen = { pitch: fundamental.pitch, score: autocorrelation(q, dilations[1], fundamental.pitch) };
    for (let p = fundamental.pitch - 1; p <= fundamental.pitch + reach + 1; p += 0.25) {
        if (p < DETECT_LIMITS.minPitch || p > maxPitch)
            continue;
        const c = { pitch: p, score: autocorrelation(q, dilations[1], p) };
        if (c.score > chosen.score)
            chosen = c;
    }
    // The prominence is still judged at the tolerance the scores were.
    chosen = { pitch: chosen.pitch, score: autocorrelation(q, qdFor(chosen.pitch), chosen.pitch) };
    // Prominence: a printed grid matches itself at its pitch far better than at a lag chosen at random; scattered
    // edges match themselves about equally at every lag, however well.
    const typicalScore = [...scores.map((x) => x.score)].sort((x, y) => x - y)[Math.floor(scores.length / 2)] ?? 0;
    const confidence = chosen.score - typicalScore;
    if (confidence < DETECT_LIMITS.minConfidence)
        return null;
    // The lines: from the strongest one, outward both ways, each looked for near where the last two found put it — so
    // a scan that stretches is followed line by line. Near is a tenth of the pitch, wider after each line not found up
    // to a quarter: a scan's spacing changes a pixel or two from one line to the next, and a window wider than that
    // lets a wall beside a faint printed line take its place (issue 1024). Walked twice: the second time a peak too
    // weak beside the typical one is a line not found, left to be derived between its neighbours.
    const pitch = chosen.pitch;
    const window = pitch / 4;
    const near = (missed) => Math.min(window, Math.max(3, pitch / 10) + missed * pitch * 0.05);
    const qs = smooth(q);
    // The walk starts on the grid, not on the strongest edge, which on a painted map may be a wall (issue 1024): the
    // offset whose teeth, a pitch apart, are strong most consistently — the median tooth, so a decorative frame's
    // heavy bands under one or two teeth cannot choose it — and the strongest peak beside its teeth.
    const qs1 = dilate(qs, 1);
    let phase = 0;
    let best = -1;
    for (let ph = 0; ph < pitch; ph += 0.5) {
        const teeth = [];
        for (let p = ph; p < n; p += pitch)
            teeth.push(qs1[Math.round(p)] ?? 0);
        teeth.sort((x, y) => x - y);
        const median = teeth[Math.floor(teeth.length / 2)] ?? 0;
        if (median > best) {
            best = median;
            phase = ph;
        }
    }
    let seed = -1;
    for (let p = phase; p < n; p += pitch) {
        for (let i = Math.max(0, Math.round(p) - 2); i <= Math.min(n - 1, Math.round(p) + 2); i += 1)
            if (seed < 0 || qs[i] > qs[seed])
                seed = i;
    }
    const walk = (floor) => {
        const out = [{ pos: refine(qs, seed), found: true, peak: qs[seed] }];
        for (const dir of [1, -1]) {
            let prev = out[0].pos;
            let step = pitch;
            let last = null;
            let missed = 0;
            for (let guard = 0; guard < GRID_LIMITS.maxLines; guard += 1) {
                const expected = prev + dir * step;
                if (expected < -window || expected > n - 1 + window)
                    break;
                const w = near(missed);
                const a = Math.max(0, Math.ceil(expected - w));
                const b = Math.min(n - 1, Math.floor(expected + w));
                let at = -1;
                let v = 0;
                for (let i = a; i <= b; i += 1)
                    if (qs[i] > v) {
                        v = qs[i];
                        at = i;
                    }
                const found = at >= 0 && v >= floor;
                const pos = found ? refine(qs, at) : expected;
                if (pos < 0 || pos > n - 1) {
                    if (!found)
                        break;
                }
                const line = { pos, found, peak: v };
                if (dir === 1)
                    out.push(line);
                else
                    out.unshift(line);
                missed = found ? 0 : missed + 1;
                if (found) {
                    // The local spacing, from the last two lines found, bounded so one misplaced peak cannot run away with it.
                    if (last !== null)
                        step = Math.max(pitch * 0.75, Math.min(pitch * 1.25, Math.abs(pos - last)));
                    last = pos;
                }
                prev = pos;
            }
        }
        return out;
    };
    const first = walk(0);
    const typical = [...first.map((l) => l.peak)].sort((x, y) => x - y)[Math.floor(first.length / 2)] ?? 0;
    const lines = walk(DETECT_LIMITS.weakPeak * typical).map((l, index) => ({ index, pos: l.pos, found: l.found }));
    // A wall's edge beside a grid line can win its window (issue 1024, an illustrated dungeon): a line found well off
    // the middle of the two found either side of it is not a line found. A drifting scan bends slowly, so its lines
    // stay near their neighbours' middle; one misplaced peak does not.
    // Where the nearest found lines put it: between the one before and the one after, or carried on from the two
    // beyond it at an end. The worst first, then again: a misplaced line pulls its neighbours' estimates too.
    const straight = Math.max(2, pitch * DETECT_LIMITS.offMiddle);
    const estimate = (k) => {
        const found = (from, dir) => { for (let j = from; j >= 0 && j < lines.length; j += dir)
            if (lines[j].found)
                return j; return -1; };
        const a = found(k - 1, -1);
        const b = found(k + 1, 1);
        const at = (i, j) => lines[i].pos + ((lines[j].pos - lines[i].pos) / (j - i)) * (k - i);
        if (a >= 0 && b >= 0)
            return at(a, b);
        const a2 = a >= 0 ? found(a - 1, -1) : -1;
        const b2 = b >= 0 ? found(b + 1, 1) : -1;
        if (a >= 0 && a2 >= 0)
            return at(a2, a);
        if (b >= 0 && b2 >= 0)
            return at(b, b2);
        return null;
    };
    for (;;) {
        let worst = -1;
        let off = straight;
        let to = 0;
        for (let k = 0; k < lines.length; k += 1) {
            if (!lines[k].found)
                continue;
            const e = estimate(k);
            if (e !== null && Math.abs(lines[k].pos - e) > off) {
                off = Math.abs(lines[k].pos - e);
                worst = k;
                to = e;
            }
        }
        if (worst < 0)
            break;
        lines[worst] = { ...lines[worst], pos: to, found: false };
    }
    const kept = lines.filter((l) => l.pos >= 0 && l.pos <= n - 1);
    const foundCount = kept.filter((l) => l.found).length;
    // Scattered furniture can line up by chance; a printed grid is lines, most of them clear.
    if (foundCount < DETECT_LIMITS.minLines || foundCount < DETECT_LIMITS.minFoundShare * kept.length)
        return null;
    // The pitch the found lines themselves set, by least squares, for the lines the editor will derive.
    const f = kept.filter((l) => l.found);
    const mi = f.reduce((s, l) => s + l.index, 0) / f.length;
    const mp = f.reduce((s, l) => s + l.pos, 0) / f.length;
    const cov = f.reduce((s, l) => s + (l.index - mi) * (l.pos - mp), 0);
    const vi = f.reduce((s, l) => s + (l.index - mi) ** 2, 0);
    const fitted = vi > 0 ? cov / vi : pitch;
    return { pitch: fitted > 0 ? fitted : pitch, phase: kept[0].pos, lines: kept, confidence };
}
export function detectGrid(img) {
    const { x, y } = edgeProfiles(img);
    return { x: detectAxis(x), y: detectAxis(y) };
}
/**
 * A detection as an axis of the fitted grid, counted from the edge the DM
 * chose: every line found is LOCKED (an anchor), everything else derived;
 * nothing but the lines changes. Index 0 is the first line from that edge.
 */
export function axisFromDetection(det, edge, extent) {
    const far = edge === 'right' || edge === 'bottom';
    const fromEdge = det.lines.map((l) => ({ pos: far ? extent - l.pos : l.pos, found: l.found })).sort((a, b) => a.pos - b.pos);
    const anchors = fromEdge.map((l, index) => ({ index, pos: Math.round(l.pos * 100) / 100, found: l.found }))
        .filter((l) => l.found).slice(0, GRID_LIMITS.maxAnchors).map(({ index, pos }) => ({ index, pos }));
    const basePitch = Math.min(GRID_LIMITS.maxPitch, Math.max(GRID_LIMITS.minPitch, Math.round(det.pitch * 1000) / 1000));
    return { edge, basePitch, anchors: anchors.length ? anchors : [{ index: 0, pos: 0 }], provisional: null };
}
//# sourceMappingURL=detect.js.map